import React, { useCallback, useEffect } from 'react'
import { Button, notification, theme, Typography } from 'antd'
import { BellOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { loadedInitSelector, userSelector, websiteIdSelector } from 'app-redux/selectors'
import { getPermissions } from 'app-redux/user/selectors'
import { registerPushDevice } from 'utils/apis'
import usePushPermission from 'utils/hooks/usePushPermission'
import { getPushTransactionPath } from 'utils/pushTransactionPath'
import {
  PUSH_NAVIGATE_MESSAGE,
  fetchFcmToken,
  getPushLinkPath,
  markPushTokenRegistered,
  shouldRegisterPushToken,
  showSystemNotification,
  subscribeForegroundMessages,
  toForegroundMessage,
  unregisterLegacyOneSignalWorkers,
} from 'utils/pushNotification'
import styles from './PushNotificationSync.module.scss'

// Layout ikon dibuat sendiri (bukan prop `icon` antd) karena kledo.scss meng-override
// `.ant-notification-notice-with-icon` secara global.
const PushNotice = ({ title, body }) => {
  const { token } = theme.useToken()

  return (
    <div className={styles.content}>
      <span
        className={styles.icon}
        style={{ background: token.colorPrimaryBg, color: token.colorPrimary }}
      >
        <BellOutlined />
      </span>
      <div className={styles.body}>
        <div className={styles.title} style={{ color: token.colorTextHeading }}>
          {title}
        </div>
        {body && (
          <div className={styles.description} style={{ color: token.colorTextSecondary }}>
            <Typography.Paragraph ellipsis={{ rows: 3 }} className="mb-0">
              {body}
            </Typography.Paragraph>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * PushNotificationSync - Sinkronisasi web push FCM untuk user yang login.
 * Berupa komponen tanpa tampilan (bukan hook) karena root layout masih class
 * component. Dipasang di root layout, bukan layout Main: saat ada tab Kledo
 * yang terlihat, SW FCM tidak menampilkan notifikasi dan hanya meneruskannya ke
 * halaman — tanpa listener di semua layout (Kasir, dll.) pesan itu hilang.
 * - melepas service worker OneSignal lama;
 * - saat izin aktif & inits termuat, mengambil token FCM dan meng-upsert ke BE
 *   (di-dedupe per token+user, di-refresh tiap 24 jam);
 * - menampilkan pesan yang masuk saat tab Kledo sedang terlihat.
 */
const PushNotificationSync = () => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { status } = usePushPermission()
  const loadedInit = useSelector(loadedInitSelector)
  const userId = useSelector(userSelector)?.id
  const permissions = useSelector(getPermissions)
  const websiteId = useSelector(websiteIdSelector)
  const isForegroundActive = !!userId && status === 'granted'
  // Registrasi menunggu inits supaya request ke BE memakai endpoint company
  // yang sudah terpilih.
  const isActive = isForegroundActive && loadedInit

  // Detail transaksi bila data push cukup untuk menyusunnya; selain itu
  // web_url dari BE (halaman Approval / Beranda).
  const resolvePushPath = useCallback(
    (data, link) =>
      getPushTransactionPath(data, { permissions, websiteId }) || getPushLinkPath(link),
    [permissions, websiteId],
  )

  useEffect(() => {
    unregisterLegacyOneSignalWorkers()
  }, [])

  // Klik notifikasi sistem: SW memfokuskan tab ini lalu mengirim data push,
  // sehingga halaman berpindah lewat router tanpa reload.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return undefined
    }
    const handleMessage = (event) => {
      if (event.data?.type !== PUSH_NAVIGATE_MESSAGE) {
        return
      }
      const path = resolvePushPath(event.data.data, event.data.link)
      if (path) {
        history.push(path)
      }
    }
    navigator.serviceWorker.addEventListener('message', handleMessage)
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage)
    }
  }, [history, resolvePushPath])

  useEffect(() => {
    if (!isActive) {
      return undefined
    }
    let cancelled = false

    const register = async () => {
      try {
        const token = await fetchFcmToken()
        if (cancelled || !token || !shouldRegisterPushToken({ token, userId })) {
          return
        }
        const { data } = await registerPushDevice({
          token,
          platform: 'web',
          app_version: import.meta.env.PACKAGE_VERSION?.slice(0, 32),
        })
        if (data?.success) {
          markPushTokenRegistered({ token, userId })
        }
      } catch (error) {
        // Gagal (izin dicabut, push service error, 400) tidak mengganggu user;
        // dicoba lagi di app start berikutnya karena penanda tidak tercatat.
        console.warn('Push notification registration failed:', error)
      }
    }
    register()

    return () => {
      cancelled = true
    }
  }, [isActive, userId])

  const showForegroundMessage = useCallback(
    (payload) => {
      // SW meneruskan pesan ke SEMUA tab bila ada satu yang terlihat. Tab
      // tersembunyi diam; tab yang terlihat yang menampilkannya.
      if (document.visibilityState !== 'visible') {
        return
      }
      const message = toForegroundMessage(payload)
      if (!message.title) {
        return
      }

      // Tab terlihat tapi window lain yang fokus → notifikasi sistem; `tag` yang
      // sama membuat OS men-dedupe bila beberapa window menerimanya.
      if (!document.hasFocus()) {
        showSystemNotification(message)
        return
      }

      // User sedang di app → toast in-app; bila ada tujuan, tombol membuka
      // halamannya lewat router (tanpa reload).
      const key = message.key || `push-${Date.now()}`
      const path = resolvePushPath(message.data, message.link)
      notification.open({
        key,
        className: styles.notice,
        message: <PushNotice title={message.title} body={message.body} />,
        placement: 'topRight',
        duration: 6,
        actions: path ? (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy(key)
              history.push(path)
            }}
          >
            {formatMessage({ id: 'notification.push_view_detail' })}
          </Button>
        ) : undefined,
      })
    },
    [formatMessage, history, resolvePushPath],
  )

  useEffect(() => {
    if (!isForegroundActive) {
      return undefined
    }
    let unsubscribe
    let cancelled = false

    subscribeForegroundMessages(showForegroundMessage)
      .then((unsubscribeFn) => {
        if (cancelled) {
          unsubscribeFn()
        } else {
          unsubscribe = unsubscribeFn
        }
      })
      .catch((error) => {
        console.warn('Push notification foreground listener failed:', error)
      })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [isForegroundActive, showForegroundMessage])

  return null
}

export default PushNotificationSync
